import { useEffect, useRef } from "react"

import { useMutation } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { UpsertPromptRequest } from "@/api/models"

import { Loader2, Sparkles } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { ProjectService } from "@/services/project-service"
import { useAISettingsStore } from "@/stores/ai-settings-store"

type PromptGeneralSettingsProps = {
  sessionType: string
  updateData: Partial<UpsertPromptRequest>
  onUpdate: (data: Partial<UpsertPromptRequest>) => void
}

export function PromptGeneralSettings({
  sessionType,
  updateData,
  onUpdate,
}: PromptGeneralSettingsProps) {
  const { t } = useTranslation("", { keyPrefix: "prompt_settings.general" })
  const isOptimizing = useRef(false)

  const optimizeInstructions = useMutation({
    mutationFn: (text: string) => ProjectService.optimizeInstructions(text),
    onMutate: () => {
      isOptimizing.current = true
    },
    onSuccess: (data) => {
      onUpdate({ content: data.optimized_instructions })
      toast.success(t("optimize_success"))
      isOptimizing.current = false
    },
    onError: () => {
      toast.error(t("optimize_error"))
      isOptimizing.current = false
    },
  })

  const initSettings = useAISettingsStore((state) => state.initSettings)
  const availableGptModels = useAISettingsStore(
    (state) => state.availableSettings.availableGptModels
  )

  useEffect(() => {
    initSettings(sessionType)
  }, [initSettings, sessionType])

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Label className="text-sm font-medium" htmlFor="prompt-title">
          {t("prompt_title")}
        </Label>
        <Input
          className="h-9"
          id="prompt-title"
          placeholder={t("prompt_title_placeholder")}
          value={updateData.title ?? ""}
          onChange={(e) => onUpdate({ title: e.target.value })}
        />
        <p className="text-muted-foreground text-xs">{t("prompt_title_description")}</p>
      </div>

      <Separator />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium" htmlFor="prompt-content">
            {t("prompt_content")}
          </Label>
          <Button
            className="gap-1.5"
            disabled={!updateData.content?.trim() || optimizeInstructions.isPending}
            size="sm"
            variant="outline"
            onClick={() => optimizeInstructions.mutate(updateData.content ?? "")}
          >
            {optimizeInstructions.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            {optimizeInstructions.isPending ? t("optimizing") : t("optimize_prompt")}
          </Button>
        </div>
        <Textarea
          className="min-h-[150px] resize-none"
          disabled={optimizeInstructions.isPending}
          id="prompt-content"
          placeholder={t("prompt_content_placeholder")}
          value={updateData.content ?? ""}
          onChange={(e) => onUpdate({ content: e.target.value })}
        />
        <p className="text-muted-foreground text-xs">{t("prompt_content_description")}</p>
      </div>

      <Separator />

      <div className="space-y-3">
        <Label className="text-sm font-medium" htmlFor="default-model">
          {t("default_ai_model")}
        </Label>
        <Select
          value={updateData.defaultModelId ?? ""}
          onValueChange={(v) => onUpdate({ defaultModelId: v || undefined })}
        >
          <SelectTrigger className="h-9" id="default-model">
            <SelectValue placeholder={t("select_model_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {availableGptModels.map((model) => (
              <SelectItem key={model.id} value={model.id}>
                <div className="flex flex-col items-start">
                  <span className="font-medium">{model.display_name || model.id}</span>
                  {model.description && (
                    <span className="text-muted-foreground text-xs">{model.description}</span>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-xs">{t("model_description")}</p>
      </div>

      <Separator />

      <div className="space-y-3">
        <div className="flex items-center justify-between rounded-md border p-3">
          <div className="space-y-0.5">
            <Label className="text-sm font-medium" htmlFor="default-high-reasoning">
              {t("enable_high_reasoning")}
            </Label>
            <p className="text-muted-foreground text-xs">
              {t("enable_high_reasoning_description")}
            </p>
          </div>
          <Switch
            checked={!!updateData.defaultHighReasoning}
            id="default-high-reasoning"
            onCheckedChange={(v) => onUpdate({ defaultHighReasoning: !!v })}
          />
        </div>
      </div>
    </div>
  )
}
